import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../rootReducer";
import { DEFAULT_SORT, ALPHABETICAL } from "../../utils/recipeSort";
import type { UnitSystem } from "../../utils/units";
import { type Locale, DEFAULT_LOCALE } from "../../i18n/locale";

export type { UnitSystem };

// UI preferences (persisted via redux-persist). Most are account-scoped and sync
// across the user's devices (see SYNCED_PREF_KEYS below); a few are genuinely
// per-device and stay local. To change whether a setting syncs, add or remove its
// key from SYNCED_PREF_KEYS — that single list is the switch.

export type CookMode = "dark" | "light";
export type ViewMode = "grid" | "list";
export type ThemeMode = "light" | "dark" | "system"; // app theme; "system" follows the OS
// Day the week starts on (0 = Sunday … 6 = Saturday). Any day is allowed so shift
// workers can align the week to their own schedule.
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6;
// How the shopping list is grouped: by grocery aisle or by source recipe.
export type ShoppingGroupBy = "aisle" | "recipe";
// How the recipe view lays out ingredients/method/notes: separate tabs, or all
// stacked on one page.
export type RecipeLayout = "tabs" | "single";

interface PreferencesState {
  cookMode: CookMode; // cook-screen theme; "dark" keeps the original look
  recipeViewMode: ViewMode; // grid/list toggle, remembered across the app
  themeMode: ThemeMode; // light/dark/system for the whole app
  weekStartsOn: WeekStart; // meal-planner week start
  shoppingGroupBy: ShoppingGroupBy; // shopping-list grouping (remembers last choice)
  recipeLayout: RecipeLayout; // recipe view: tabs vs single scrolling page
  unitSystem: UnitSystem; // display units: as-written, or auto-convert to metric/imperial
  // Show/hide recipe meta throughout the app (cards, lists, rails, detail).
  showDuration: boolean;
  showDifficulty: boolean;
  showServings: boolean;
  showRating: boolean;
  showTags: boolean;
  // Whether the cook-mode timer starts expanded (true) or collapsed to a clock icon
  // (false). The timer is always available either way — this only sets the default;
  // the cook can collapse/reopen it during a cook.
  autoShowTimer: boolean;
  // When the cook-mode timer reaches zero: play a sound (web) / vibrate (phone),
  // and/or flash the timer. Each is independently toggleable.
  timerSound: boolean;
  timerFlash: boolean;
  // iOS: play the timer sound even when the ringer is on silent (like a clock
  // alarm). Off → respect the silent switch. iOS-only; web/Android ignore it.
  timerSoundOnSilent: boolean;
  // Whether tactile feedback (vibration/haptics) fires on taps, selections and
  // confirmations throughout the app. On by default; turn off for a silent, still
  // experience (sensory preference / accessibility). There is no OS-level "reduce
  // haptics" flag an app can read, so this in-app toggle is the control.
  hapticsEnabled: boolean;
  // Per-recipe layout overrides (recipeId -> layout) that win over recipeLayout.
  recipeLayoutOverrides: Record<string, RecipeLayout>;
  // Sort choice shared across the general recipe listings (library, favourites,
  // quick recipes). One preference remembered per device across those screens.
  recipeSort: string;
  // Per-collection sort choice (collectionId -> option). Each collection keeps
  // its own; absent → falls back to ALPHABETICAL (see selectCollectionSort).
  collectionSort: Record<string, string>;
  // The shopping list currently shown (device-local, NOT synced). Drives the
  // shopping screen, the homepage peek and the default add-to-list target. Null →
  // falls back to the primary list (see selectActiveListId in shoppingListsSlice).
  activeShoppingListId: string | null;
  // UI language/spelling (device-local, NOT synced). The *resolved effective*
  // locale that drives t(); LocaleBootstrap derives it from localeMode below.
  // "en" is the AU/UK base; "en-US" swaps spelling.
  locale: Locale;
  // The user's language choice: "system" follows the device locale (default);
  // any specific Locale pins it regardless of device. Device-local, NOT synced.
  localeMode: LocaleMode;
  // Whether the first-run intro carousel has been shown. Device-local (NOT synced)
  // so the intro plays once per device; false → OnboardingGate shows it after login.
  hasSeenOnboarding: boolean;
  // Whether the user has consented to anonymous usage analytics (PostHog). Synced,
  // so the choice follows the account to every device (and turning it off anywhere
  // turns it off everywhere). False by default: nothing is captured until the user
  // opts in. During the pre-pull window the local default keeps a device opted out.
  analyticsConsent: boolean;
  // Review-prompt bookkeeping. Device-local (NOT synced) — the native review sheet
  // is throttled per device, so the "have we asked here lately" state belongs on
  // the device, not the account. See utils/reviewPrompt + the mobile UpdateGate's
  // sibling logic in cook mode.
  lastReviewPromptVersion: string | null; // app version at the last ask
  lastReviewPromptAt: number | null; // epoch ms of the last ask
  reviewPromptCount: number; // lifetime asks on this device
  // Which home-screen cards are shown, and in what order (see the Customise home
  // screen). Both synced, so a tidied, reordered home follows the user across
  // devices. All default on + the default order; hiding a card never removes the
  // feature (header icons + tabs remain). See HOME_CARDS for the set.
  // (QuickAdd is intentionally NOT customisable — it's an onboarding nudge that
  // self-manages: phone-only and auto-hides once you have a few recipes.)
  homeCards: Record<HomeCard, boolean>;
  homeCardOrder: HomeCard[];
}

// Home-screen cards the user can show/hide + reorder, in default display order.
export type HomeCard =
  | "search"
  | "continueCooking"
  | "shoppingList"
  | "whatCanIMake"
  | "mealPlan"
  | "collections"
  | "cookItAgain"
  | "quickRecipes"
  | "recentlyAdded"
  | "recentlyViewed";

export const HOME_CARDS: HomeCard[] = [
  "search",
  "continueCooking",
  "shoppingList",
  "whatCanIMake",
  "mealPlan",
  "collections",
  "cookItAgain",
  "quickRecipes",
  "recentlyAdded",
  "recentlyViewed",
];

// Default visibility. Most cards start on; Recently added is the default pick,
// with Recently viewed off (the two cover similar ground, so only one shows by
// default). Users can flip either in Customise home.
const HOME_CARD_DEFAULT_VISIBLE: Record<HomeCard, boolean> = {
  search: true,
  continueCooking: true,
  shoppingList: true,
  whatCanIMake: true,
  mealPlan: true,
  collections: true,
  cookItAgain: true,
  quickRecipes: true,
  recentlyAdded: true,
  recentlyViewed: false,
};

const defaultHomeCards = (): Record<HomeCard, boolean> => ({ ...HOME_CARD_DEFAULT_VISIBLE });

// "system" = follow the device locale; a Locale value pins that language.
export type LocaleMode = "system" | Locale;

// Field keys for the recipe-meta visibility toggles.
export type RecipeMetaField =
  | "showDuration"
  | "showDifficulty"
  | "showServings"
  | "showRating"
  | "showTags";

// Exported so the redux-persist migration can backfill any fields that are missing
// from state persisted before they were added (see makeStore migrations).
export const preferencesInitialState: PreferencesState = {
  cookMode: "dark",
  recipeViewMode: "grid",
  themeMode: "system",
  weekStartsOn: 1,
  shoppingGroupBy: "aisle",
  recipeLayout: "tabs",
  unitSystem: "original",
  showDuration: true,
  showDifficulty: true,
  showServings: true,
  showRating: true,
  showTags: true,
  autoShowTimer: true,
  timerSound: true,
  timerFlash: true,
  timerSoundOnSilent: true,
  hapticsEnabled: true,
  recipeLayoutOverrides: {},
  recipeSort: DEFAULT_SORT,
  collectionSort: {},
  activeShoppingListId: null,
  locale: DEFAULT_LOCALE,
  localeMode: "system",
  hasSeenOnboarding: false,
  analyticsConsent: false,
  lastReviewPromptVersion: null,
  lastReviewPromptAt: null,
  reviewPromptCount: 0,
  homeCards: defaultHomeCards(),
  homeCardOrder: [...HOME_CARDS],
};

const initialState: PreferencesState = preferencesInitialState;

// The preferences that sync per-account (follow the user across devices). Anything
// NOT listed here is device-local: `cookMode` (transient cook-screen theme),
// `recipeViewMode` (grid/list suits the device — a wide tablet wants grid, a phone
// wants list), and `locale` (the *resolved* locale, re-derived on each device from
// the synced `localeMode`). Add a key here to start syncing a setting; remove it
// to make it device-local.
export const SYNCED_PREF_KEYS = [
  "themeMode",
  "localeMode",
  "unitSystem",
  "weekStartsOn",
  "shoppingGroupBy",
  "recipeLayout",
  "recipeLayoutOverrides",
  "showDuration",
  "showDifficulty",
  "showServings",
  "showRating",
  "showTags",
  "autoShowTimer",
  "timerSound",
  "timerFlash",
  "timerSoundOnSilent",
  "hapticsEnabled",
  "recipeSort",
  "collectionSort",
  "activeShoppingListId",
  "hasSeenOnboarding",
  "analyticsConsent",
  "homeCards",
  "homeCardOrder",
] as const satisfies readonly (keyof PreferencesState)[];

// The account-synced subset of preferences (what push/pull transports as a blob).
export type SyncedPreferences = Pick<
  PreferencesState,
  (typeof SYNCED_PREF_KEYS)[number]
>;

export const preferencesSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {
    setCookMode(state, action: PayloadAction<CookMode>) {
      state.cookMode = action.payload;
    },
    toggleCookMode(state) {
      state.cookMode = state.cookMode === "dark" ? "light" : "dark";
    },
    setRecipeViewMode(state, action: PayloadAction<ViewMode>) {
      state.recipeViewMode = action.payload;
    },
    setThemeMode(state, action: PayloadAction<ThemeMode>) {
      state.themeMode = action.payload;
    },
    setWeekStartsOn(state, action: PayloadAction<WeekStart>) {
      state.weekStartsOn = action.payload;
    },
    setShoppingGroupBy(state, action: PayloadAction<ShoppingGroupBy>) {
      state.shoppingGroupBy = action.payload;
    },
    setRecipeLayout(state, action: PayloadAction<RecipeLayout>) {
      state.recipeLayout = action.payload;
    },
    setUnitSystem(state, action: PayloadAction<UnitSystem>) {
      state.unitSystem = action.payload;
    },
    // Whether the cook-mode timer starts expanded vs collapsed (Preferences toggle).
    setAutoShowTimer(state, action: PayloadAction<boolean>) {
      state.autoShowTimer = action.payload;
    },
    setTimerSound(state, action: PayloadAction<boolean>) {
      state.timerSound = action.payload;
    },
    setTimerFlash(state, action: PayloadAction<boolean>) {
      state.timerFlash = action.payload;
    },
    setTimerSoundOnSilent(state, action: PayloadAction<boolean>) {
      state.timerSoundOnSilent = action.payload;
    },
    // Turn tactile feedback (haptics) on/off app-wide (Preferences toggle).
    setHapticsEnabled(state, action: PayloadAction<boolean>) {
      state.hapticsEnabled = action.payload;
    },
    setRecipeMetaVisible(
      state,
      action: PayloadAction<{ field: RecipeMetaField; value: boolean }>,
    ) {
      state[action.payload.field] = action.payload.value;
    },
    // Pin a recipe to a layout (or pass null to clear → follow the global pref).
    setRecipeLayoutOverride(
      state,
      action: PayloadAction<{ recipeId: string; layout: RecipeLayout | null }>,
    ) {
      const { recipeId, layout } = action.payload;
      if (!state.recipeLayoutOverrides) state.recipeLayoutOverrides = {};
      if (layout === null) delete state.recipeLayoutOverrides[recipeId];
      else state.recipeLayoutOverrides[recipeId] = layout;
    },
    // Shared sort for library/favourites/quick recipes.
    setRecipeSort(state, action: PayloadAction<string>) {
      state.recipeSort = action.payload;
    },
    // A single collection's sort choice (keyed by its id).
    setCollectionSort(
      state,
      action: PayloadAction<{ collectionId: string; option: string }>,
    ) {
      if (!state.collectionSort) state.collectionSort = {};
      state.collectionSort[action.payload.collectionId] = action.payload.option;
    },
    // Switch which shopping list is active (the one shown everywhere).
    setActiveShoppingList(state, action: PayloadAction<string>) {
      state.activeShoppingListId = action.payload;
    },
    // Set the resolved effective UI locale (LocaleBootstrap derives this from
    // localeMode + the device locale). Drives t().
    setLocale(state, action: PayloadAction<Locale>) {
      state.locale = action.payload;
    },
    // Set the user's language choice: "system" to follow the device, or a
    // specific Locale to pin it. LocaleBootstrap re-derives `locale` from this.
    setLocaleMode(state, action: PayloadAction<LocaleMode>) {
      state.localeMode = action.payload;
    },
    // Mark the first-run intro as seen (true) or replay it (false, from Preferences).
    setHasSeenOnboarding(state, action: PayloadAction<boolean>) {
      state.hasSeenOnboarding = action.payload;
    },
    // Opt in/out of anonymous usage analytics. Device-local (NOT synced).
    setAnalyticsConsent(state, action: PayloadAction<boolean>) {
      state.analyticsConsent = action.payload;
    },
    // Record that we showed the native review prompt on this device: stamp the
    // app version + time and bump the lifetime count, so the cooldown / once-per-
    // version / give-up guardrails in shouldRequestReview have state to work from.
    recordReviewPrompt(state, action: PayloadAction<{ version: string; at: number }>) {
      state.lastReviewPromptVersion = action.payload.version;
      state.lastReviewPromptAt = action.payload.at;
      state.reviewPromptCount += 1;
    },
    // Show/hide a home-screen card (see the "Customise home" screen). Hiding a card
    // only tidies the dashboard; the feature stays reachable from the header + tabs.
    setHomeCardVisible(state, action: PayloadAction<{ card: HomeCard; visible: boolean }>) {
      state.homeCards[action.payload.card] = action.payload.visible;
    },
    // Set the home-card display order (from the drag-to-reorder list).
    setHomeCardOrder(state, action: PayloadAction<HomeCard[]>) {
      state.homeCardOrder = action.payload;
    },
    // "Reset to default layout" — restore the stock order and show every card.
    resetHomeLayout(state) {
      state.homeCardOrder = [...HOME_CARDS];
      state.homeCards = defaultHomeCards();
    },
    // Merge a synced-preferences blob pulled from the server. Tagged meta.fromSync
    // (see prepare) so the dirty-tracker doesn't re-flag it for push (echo loop).
    // Only known synced keys are applied; unknown keys in the blob are ignored.
    applySyncedPreferences: {
      reducer(state, action: PayloadAction<Partial<SyncedPreferences>>) {
        for (const key of SYNCED_PREF_KEYS) {
          const value = action.payload[key];
          if (value === undefined) continue;
          // hasSeenOnboarding is monotonic: once true for an account it must never
          // flow back to false via sync. A stale/false server blob — one pushed on
          // first run before the intro was dismissed, or by the dev "replay" toggle
          // — would otherwise re-flip a device that has already onboarded and
          // re-show the tour on the next pull. Only ever let sync move it true.
          if (key === "hasSeenOnboarding" && value === false) continue;
          (state[key] as unknown) = value;
        }
      },
      prepare(data: Partial<SyncedPreferences>) {
        return { payload: data, meta: { fromSync: true } };
      },
    },
  },
});

export const {
  setCookMode,
  toggleCookMode,
  setRecipeViewMode,
  setThemeMode,
  setWeekStartsOn,
  setShoppingGroupBy,
  setRecipeLayout,
  setUnitSystem,
  setAutoShowTimer,
  setTimerSound,
  setTimerFlash,
  setTimerSoundOnSilent,
  setHapticsEnabled,
  setRecipeMetaVisible,
  setRecipeLayoutOverride,
  setRecipeSort,
  setCollectionSort,
  setActiveShoppingList,
  setLocale,
  setLocaleMode,
  setHasSeenOnboarding,
  setAnalyticsConsent,
  recordReviewPrompt,
  setHomeCardVisible,
  setHomeCardOrder,
  resetHomeLayout,
  applySyncedPreferences,
} = preferencesSlice.actions;

// The account-synced subset of the current preferences — what the sync layer
// pushes to the server as a single blob.
export const selectSyncedPreferences = (s: RootState): SyncedPreferences => {
  const p = s.preferences;
  const out = {} as Record<string, unknown>;
  for (const key of SYNCED_PREF_KEYS) out[key] = p[key];
  return out as SyncedPreferences;
};

// Home-card visibility; falls back to all-on when absent (older persisted state).
export const selectHomeCards = (s: RootState): Record<HomeCard, boolean> =>
  s.preferences.homeCards ?? defaultHomeCards();

// Effective home-card order. Reconciled against HOME_CARDS so it survives added or
// removed cards: keeps the stored order for known cards, drops unknown ones, and
// appends any card missing from the stored order (new cards land at the end).
// Memoized: it builds a fresh array (reconciliation), so an unmemoized selector
// returns a new reference every call and makes React-Redux warn / rerender.
export const selectHomeCardOrder = createSelector(
  (s: RootState) => s.preferences.homeCardOrder,
  (stored): HomeCard[] => {
    const valid = (stored ?? []).filter((c): c is HomeCard =>
      HOME_CARDS.includes(c as HomeCard),
    );
    const missing = HOME_CARDS.filter((c) => !valid.includes(c));
    return [...valid, ...missing];
  },
);

export const selectCookMode = (s: RootState) => s.preferences.cookMode;
// UI locale; defaults to the base when absent (state persisted before it existed).
export const selectLocale = (s: RootState): Locale =>
  s.preferences.locale ?? DEFAULT_LOCALE;
// The user's language choice; "system" (follow device) when absent.
export const selectLocaleMode = (s: RootState): LocaleMode =>
  s.preferences.localeMode ?? "system";
// First-run intro flag; false (not yet seen) when absent — so the intro shows for
// existing installs on first launch after this ships, then never again.
export const selectHasSeenOnboarding = (s: RootState): boolean =>
  s.preferences.hasSeenOnboarding ?? false;
export const selectRecipeViewMode = (s: RootState) => s.preferences.recipeViewMode;
// Fall back to "system" if the field is absent — e.g. state persisted before
// themeMode existed and rehydrated by autoMergeLevel1 (which doesn't backfill new
// fields). Guards against colorScheme.set(undefined) leaving a half-applied theme.
export const selectThemeMode = (s: RootState): ThemeMode =>
  s.preferences.themeMode ?? "system";
// Default Monday when absent (state persisted before this field existed).
export const selectWeekStartsOn = (s: RootState): WeekStart =>
  s.preferences.weekStartsOn ?? 1;
// Default to aisle grouping when absent (state persisted before this field existed).
export const selectShoppingGroupBy = (s: RootState): ShoppingGroupBy =>
  s.preferences.shoppingGroupBy ?? "aisle";
// Default to tabs when absent (state persisted before this field existed).
export const selectRecipeLayout = (s: RootState): RecipeLayout =>
  s.preferences.recipeLayout ?? "tabs";
// Display unit system; defaults to "original" (no conversion) when absent.
export const selectUnitSystem = (s: RootState): UnitSystem =>
  s.preferences.unitSystem ?? "original";
// A recipe's explicit layout override (undefined when it follows the global pref).
export const selectRecipeLayoutOverride = (
  s: RootState,
  recipeId: string,
): RecipeLayout | undefined => s.preferences.recipeLayoutOverrides?.[recipeId];
// The layout to actually use for a recipe: its override, else the global pref.
export const selectEffectiveRecipeLayout = (
  s: RootState,
  recipeId: string,
): RecipeLayout =>
  s.preferences.recipeLayoutOverrides?.[recipeId] ?? selectRecipeLayout(s);

// Recipe-meta visibility (default on when absent). The combined selector is
// memoised so consumers can destructure without re-rendering every store change.
export const selectShowDuration = (s: RootState): boolean =>
  s.preferences.showDuration ?? true;
export const selectShowDifficulty = (s: RootState): boolean =>
  s.preferences.showDifficulty ?? true;
export const selectShowServings = (s: RootState): boolean =>
  s.preferences.showServings ?? true;
export const selectShowRating = (s: RootState): boolean =>
  s.preferences.showRating ?? true;
export const selectShowTags = (s: RootState): boolean =>
  s.preferences.showTags ?? true;
// Whether the cook-mode timer auto-opens (vs starts collapsed); defaults on.
export const selectAutoShowTimer = (s: RootState): boolean =>
  s.preferences.autoShowTimer ?? true;
// Timer-finished alerts; default on when absent.
export const selectTimerSound = (s: RootState): boolean =>
  s.preferences.timerSound ?? true;
export const selectTimerFlash = (s: RootState): boolean =>
  s.preferences.timerFlash ?? true;
// iOS: play the timer sound through the silent switch; defaults on.
export const selectTimerSoundOnSilent = (s: RootState): boolean =>
  s.preferences.timerSoundOnSilent ?? true;
// Tactile feedback (haptics) app-wide; defaults on when absent (state persisted
// before this field existed).
export const selectHapticsEnabled = (s: RootState): boolean =>
  s.preferences.hapticsEnabled ?? true;
// Shared sort for the general recipe listings; defaults to DEFAULT_SORT when
// absent (state persisted before this field existed).
export const selectRecipeSort = (s: RootState): string =>
  s.preferences.recipeSort ?? DEFAULT_SORT;
// A collection's own sort choice, defaulting to ALPHABETICAL when it has none.
export const selectCollectionSort = (s: RootState, collectionId: string): string =>
  s.preferences.collectionSort?.[collectionId] ?? ALPHABETICAL;

export const selectRecipeMetaVisibility = createSelector(
  [
    selectShowDuration,
    selectShowDifficulty,
    selectShowServings,
    selectShowRating,
    selectShowTags,
  ],
  (duration, difficulty, servings, rating, tags) => ({
    duration,
    difficulty,
    servings,
    rating,
    tags,
  }),
);

export default preferencesSlice.reducer;
